"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import Amgen from "public/images/auth/customers/amgen.png"
import Capintel from "public/images/auth/customers/capintel.png"
import Cherry from "public/images/auth/customers/cherry.png"
import Ciena from "public/images/auth/customers/ciena.png"
import Mutualink from "public/images/auth/customers/mutualink.png"
import VantageBank from "public/images/auth/customers/vantage-bank.png"
import RhythmsWordmark from "public/images/auth/rhythms-wordmark.png"

import { SignupFlowLines } from "@/components/auth/signup-flow-lines"
import { cn } from "@/lib/utils"

interface Testimonial {
  quote: string
  attribution: string
}

const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "Rhythms' AI engine keeps our team focused on executing priorities. Onboarding was more of a partnership than an implementation.",
    attribution: "Founding Partner & COO",
  },
  {
    quote:
      "I used Chat to pull our Key Result metrics and build a month-by-month composite network health score — documented, defensible, and reused for every assessment since.",
    attribution: "Sr. Director, Infrastructure Operations",
  },
]

// Intrinsic (tightly trimmed) pixel dimensions are supplied so every logo
// renders at the same height with an undistorted, auto width — keeping the
// wall visually uniform regardless of each source asset's aspect ratio.
const CUSTOMER_LOGOS = [
  { src: Amgen, alt: "Amgen", width: 158, height: 40 },
  { src: Cherry, alt: "Cherry", width: 204, height: 56 },
  { src: VantageBank, alt: "Vantage Bank", width: 275, height: 48 },
  { src: Capintel, alt: "Capintel", width: 195, height: 42 },
  { src: Mutualink, alt: "Mutualink", width: 218, height: 39 },
  { src: Ciena, alt: "Ciena", width: 161, height: 51 },
]

const ROTATE_INTERVAL_MS = 6000

// Union of soft radial blobs used as a mask so the flow-field is only visible
// in a handful of drifting zones and fades to nothing elsewhere.
const PATTERN_MASK = [
  "radial-gradient(52% 32% at 26% 16%, #000 0%, rgba(0,0,0,0) 70%)",
  "radial-gradient(46% 30% at 82% 32%, #000 0%, rgba(0,0,0,0) 70%)",
  "radial-gradient(58% 32% at 44% 54%, #000 0%, rgba(0,0,0,0) 72%)",
].join(", ")

export function SignupMarketingPanel({ className }: { className?: string }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const sync = () => {
      setReduceMotion(query.matches)
    }
    sync()
    query.addEventListener("change", sync)
    return () => {
      query.removeEventListener("change", sync)
    }
  }, [])

  // Depending on `activeIndex` restarts the timer after every change, so a
  // manual selection also gets the full dwell time. Auto-advance is suspended
  // while hovered/focused (WCAG 2.2.2 pause) and when reduced motion is set.
  useEffect(() => {
    if (isPaused || reduceMotion || TESTIMONIALS.length <= 1) return
    const timer = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % TESTIMONIALS.length)
    }, ROTATE_INTERVAL_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [activeIndex, isPaused, reduceMotion])

  const activeTestimonial = TESTIMONIALS[activeIndex]

  if (!activeTestimonial) return null

  return (
    <div
      className={cn(
        "text-content-primary relative flex flex-col overflow-hidden p-12",
        className
      )}
      style={{
        // Soft white "light spot" halo sits behind the testimonial card, over a
        // lavender wash that starts light at the top and deepens toward the
        // bottom — echoing the rhythms.ai marketing gradient.
        backgroundImage: [
          "radial-gradient(60% 45% at 50% 50%, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0) 70%)",
          "radial-gradient(90% 70% at 50% 120%, rgba(233, 213, 255, 0.55) 0%, rgba(233, 213, 255, 0) 60%)",
          "linear-gradient(180deg, #faf5ff 0%, #f1e8fe 50%, #e4d7fb 100%)",
        ].join(", "),
      }}
    >
      {/* Subtle procedural flow-field of lines behind the content. A soft
          multi-blob mask lets the lines surface in a few zones and dissolve to
          nothing in between, so the texture never reads as a uniform tile. */}
      <SignupFlowLines
        className="z-0 opacity-[0.14]"
        maskImage={PATTERN_MASK}
      />

      {/* Top zone: wordmark pinned to the top with the headline centered in the
          space between it and the (vertically centered) testimonial card. Uses
          flex-1 to balance the equal-height bottom zone below the card. */}
      <div className="relative z-10 flex flex-1 flex-col">
        <div className="flex justify-center pt-2">
          <Image
            src={RhythmsWordmark}
            alt="Rhythms"
            width={1024}
            height={299}
            className="h-11 w-auto"
          />
        </div>
        <div className="flex flex-1 items-center justify-center">
          {/* Rendered as a paragraph (not a heading) so it doesn't precede the
              form's page-level h1 in the document heading order. */}
          <p className="text-content-primary text-center text-[clamp(1.6rem,3vw,2.25rem)] leading-[1.15] font-extrabold tracking-tight text-nowrap">
            The AI operating partner{" "}
            <span className="text-brand block">
              that runs your business rhythms
            </span>
          </p>
        </div>
      </div>

      {/* Testimonial card — vertically centered in the panel because the top and
          bottom zones are equal flex-1, so it lines up with the login card on
          the right at any viewport height. */}
      <div className="relative z-10 flex shrink-0 justify-center py-6">
        <figure
          className="bg-surface-primary/80 relative z-10 w-full max-w-xl rounded-2xl border border-black/[0.06] px-8 py-6 text-center shadow-[0_18px_48px_-24px_rgba(76,29,149,0.35)] backdrop-blur-sm"
          onMouseEnter={() => {
            setIsPaused(true)
          }}
          onMouseLeave={() => {
            setIsPaused(false)
          }}
          onFocus={() => {
            setIsPaused(true)
          }}
          onBlur={() => {
            setIsPaused(false)
          }}
        >
          <div aria-live="polite" aria-atomic="true">
            <blockquote className="text-content-primary mx-auto flex min-h-[104px] items-center justify-center text-base leading-relaxed font-medium text-balance">
              &ldquo;{activeTestimonial.quote}&rdquo;
            </blockquote>

            <figcaption className="mt-4">
              <span className="bg-brand/40 mx-auto mb-3 block h-px w-8" />
              <span className="text-content-tertiary text-xs font-semibold tracking-[0.14em] uppercase">
                {activeTestimonial.attribution}
              </span>
            </figcaption>
          </div>

          <div
            className="mt-4 flex justify-center gap-2"
            role="group"
            aria-label="Choose testimonial"
          >
            {TESTIMONIALS.map((testimonial, index) => (
              <button
                key={testimonial.attribution}
                type="button"
                aria-current={index === activeIndex ? "true" : undefined}
                aria-label={`Show testimonial ${index + 1}`}
                onClick={() => {
                  setActiveIndex(index)
                }}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  index === activeIndex
                    ? "bg-brand w-6"
                    : "w-1.5 bg-black/15 hover:bg-black/30"
                )}
              />
            ))}
          </div>
        </figure>
      </div>

      {/* Bottom zone: customer logos pinned toward the bottom, mirroring the top
          zone's flex-1 so the card stays centered. */}
      <div className="relative z-10 flex flex-1 flex-col justify-end">
        <p className="text-content-tertiary mb-6 text-center text-xs font-medium tracking-[0.2em] uppercase">
          Trusted by teams at
        </p>
        <div className="mx-auto grid max-w-sm grid-cols-3 place-items-center gap-x-8 gap-y-6">
          {CUSTOMER_LOGOS.map((logo) => (
            <Image
              key={logo.alt}
              src={logo.src}
              alt={logo.alt}
              width={logo.width}
              height={logo.height}
              className="h-6 w-auto object-contain opacity-60 grayscale"
            />
          ))}
        </div>
      </div>
    </div>
  )
}
